import { useState } from 'react';
import { UserPlus, MailCheck, RefreshCw } from 'lucide-react';
import {
  useContactPortalAccess,
  useInviteClient,
  useResendClientInvitation,
} from '../../hooks/queries/usePortalInvite';
import { useCanAccessFeature } from '../../hooks/usePermissions';

interface Props {
  contactId: string;
  /** Style compact pour l'usage en ligne dans la liste des parties. */
  compact?: boolean;
}

export function InviteClientButton({ contactId, compact = false }: Props) {
  const canInvite = useCanAccessFeature('client_portal_invite');
  const { data: hasAccess, isLoading } = useContactPortalAccess(contactId);
  const invite = useInviteClient();
  const resend = useResendClientInvitation();
  const [msg, setMsg] = useState<string | null>(null);

  if (!canInvite || isLoading) return null;

  function handleInvite() {
    setMsg(null);
    invite.mutate(contactId, {
      onSuccess: () => setMsg('Invitation envoyée.'),
      onError: (e) =>
        setMsg(e instanceof Error ? e.message : 'Échec de l\'invitation.'),
    });
  }

  function handleResend() {
    setMsg(null);
    resend.mutate(contactId, {
      onSuccess: () => setMsg('Invitation renvoyée.'),
      onError: (e) =>
        setMsg(e instanceof Error ? e.message : 'Échec de l\'envoi.'),
    });
  }

  const base = compact
    ? 'inline-flex items-center gap-1.5 text-xs'
    : 'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border';

  if (hasAccess) {
    return (
      <div className="flex flex-col gap-1">
        <span className={`${base} text-cime-text-muted border-neutral-200`}>
          <MailCheck className="w-4 h-4" /> A un accès portail
          <button
            type="button"
            onClick={handleResend}
            disabled={resend.isPending}
            className="ml-1 text-cime-primary hover:underline inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Renvoyer
          </button>
        </span>
        {msg && <span className="text-xs text-cime-text-muted">{msg}</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleInvite}
        disabled={invite.isPending}
        className={`${base} border-cime-primary text-cime-primary hover:bg-cime-primary/5`}
      >
        <UserPlus className="w-4 h-4" />
        {invite.isPending ? 'Envoi…' : 'Inviter au portail client'}
      </button>
      {msg && <span className="text-xs text-cime-text-muted">{msg}</span>}
    </div>
  );
}
